/**
 * Boundary translation between the marketplace UI's flat
 * `PropertySearchQuery` filter shape and Athena's product-shaped
 * `SavedSearchInput` / `SavedSearchFieldsFragment`.
 *
 * These functions are pure: no React, no I/O. The hook and UI components
 * compose them with `useSearchState` to round-trip filters.
 */
import type {
  SavedSearchFieldsFragment,
  SavedSearchInput,
  SavedSearchPropertyType,
} from '__generated__/athena';

import type { PropertySearchQuery } from '../helpers/useSearchQueries';
import type { GeoPoint } from '../map/types';

export const SUPPORTED_PROPERTY_TYPES: ReadonlyArray<SavedSearchPropertyType> = [
  'HOME',
  'CONDO',
  'TOWNHOME',
];

const buildPropertyTypes = (query: PropertySearchQuery): SavedSearchPropertyType[] => {
  const types: SavedSearchPropertyType[] = [];
  if (query.singleFamily) {
    types.push('HOME');
  }
  if (query.condo) {
    types.push('CONDO');
  }
  if (query.townhome) {
    types.push('TOWNHOME');
  }
  // Collapse "all selected" to "any" so the persisted search behaves like
  // the UI default state (matches `getInitialPropertyTypes` in useSearchState.ts).
  return types.length === SUPPORTED_PROPERTY_TYPES.length ? [] : types;
};

// The Cosmos UI exposes a strict subset of `SavedSearchFilters` (no upper
// bounds for beds/baths, no Opendoor-owned or FHA toggles, and HOA is a
// single `noHoas` boolean rather than a numeric cap). Without preserving the
// caller-provided previous record, an update from Cosmos would silently
// clobber whatever another client (mobile, athlete-fe) stored in those
// fields. The `previous` parameter is the existing `SavedSearchFieldsFragment`
// being edited; pass `null`/omit on the initial add path.
const resolveHoaCap = (
  noHoas: boolean | null | undefined,
  previousHoaCap: number | null | undefined,
): number | null => {
  if (noHoas) {
    return 0;
  }
  // `noHoas: false` in Cosmos can mean either "no HOA filter" or "I never
  // saw an HOA filter to toggle off" — both render identically in the UI.
  // If the previous record had a finite non-zero cap (e.g. $500), preserve
  // it so editing an unrelated field doesn't wipe out an HOA cap set on
  // another surface.
  if (previousHoaCap != null && previousHoaCap > 0) {
    return previousHoaCap;
  }
  return null;
};

export function toSavedSearchInput(
  query: PropertySearchQuery,
  name: string,
  previous?: SavedSearchFieldsFragment | null,
): SavedSearchInput {
  const previousFilters = previous?.filters;
  return {
    name,
    location: {
      market: query.market ?? null,
      zoneIds: query.zoneIds ?? null,
      vertices: query.vertices ?? null,
    },
    filters: {
      minPriceDollars: query.minListPrice ?? null,
      maxPriceDollars: query.maxListPrice ?? null,
      maxMonthlyHoaFeeDollars: resolveHoaCap(
        query.noHoas,
        previousFilters?.maxMonthlyHoaFeeDollars,
      ),
      minBedrooms: query.bedrooms ?? null,
      // Cosmos has no UI for max-bound bed/bath filters; preserve any value
      // set by another client instead of clobbering it with `null` on update.
      maxBedrooms: previousFilters?.maxBedrooms ?? null,
      minBathrooms: query.bathrooms ?? null,
      maxBathrooms: previousFilters?.maxBathrooms ?? null,
      minSqft: query.minSquareFootage ?? null,
      maxSqft: query.maxSquareFootage ?? null,
      minLotSqft: query.minLotSize ?? null,
      maxLotSqft: query.maxLotSize ?? null,
      minYearBuilt: query.minYearBuilt ?? null,
      maxYearBuilt: query.maxYearBuilt ?? null,
      // Same rationale as max bed/bath: no Cosmos UI, so preserve on update.
      opendoorOwnedOnly: previousFilters?.opendoorOwnedOnly ?? false,
      selfTourableOnly: query.canSelfTour ?? false,
      singleStoryOnly: query.hasSingleStory ?? false,
      hasPoolOnly: query.hasPool ?? false,
      fhaEligibleOnly: previousFilters?.fhaEligibleOnly ?? false,
      propertyTypes: buildPropertyTypes(query),
    },
  };
}

export function toPropertySearchQuery(saved: SavedSearchFieldsFragment): PropertySearchQuery {
  const types = saved.filters.propertyTypes;
  const allOrNone = types.length === 0 || types.length === SUPPORTED_PROPERTY_TYPES.length;
  const vertices: GeoPoint[] | undefined =
    saved.location.vertices.length > 0
      ? saved.location.vertices.map((v) => ({ lat: v.lat, lon: v.lon }))
      : undefined;
  return {
    market: saved.location.market ?? undefined,
    zoneIds: saved.location.zoneIds.length > 0 ? [...saved.location.zoneIds] : undefined,
    vertices,
    bedrooms: saved.filters.minBedrooms ?? null,
    bathrooms: saved.filters.minBathrooms ?? null,
    minListPrice: saved.filters.minPriceDollars ?? null,
    maxListPrice: saved.filters.maxPriceDollars ?? null,
    minSquareFootage: saved.filters.minSqft ?? null,
    maxSquareFootage: saved.filters.maxSqft ?? null,
    minLotSize: saved.filters.minLotSqft ?? null,
    maxLotSize: saved.filters.maxLotSqft ?? null,
    minYearBuilt: saved.filters.minYearBuilt ?? null,
    maxYearBuilt: saved.filters.maxYearBuilt ?? null,
    noHoas: saved.filters.maxMonthlyHoaFeeDollars === 0,
    canSelfTour: saved.filters.selfTourableOnly || null,
    hasSingleStory: saved.filters.singleStoryOnly || null,
    hasPool: saved.filters.hasPoolOnly || null,
    singleFamily: allOrNone || types.includes('HOME'),
    condo: allOrNone || types.includes('CONDO'),
    townhome: allOrNone || types.includes('TOWNHOME'),
  };
}

export function formatSavedSearchName(query: PropertySearchQuery): string {
  const parts: string[] = [];
  if (query.bedrooms) {
    parts.push(`${query.bedrooms}+ bd`);
  }
  if (query.minListPrice || query.maxListPrice) {
    const minK = query.minListPrice ? `$${Math.round(query.minListPrice / 1000)}k` : '';
    const maxK = query.maxListPrice ? `$${Math.round(query.maxListPrice / 1000)}k` : '';
    parts.push([minK, maxK].filter(Boolean).join('–'));
  }
  if (query.hasPool) {
    parts.push('pool');
  }
  if (query.market) {
    parts.push(`in ${formatMarketSlug(query.market)}`);
  }
  return parts.length > 0 ? parts.join(', ') : 'My saved search';
}

// Opendoor market slugs are lowercase tokens joined by hyphens or underscores
// (`san-antonio`, `los_angeles`) — both separators are canonical (see
// CANONICAL_MARKET_SLUG in SavedSearchesDropdown.tsx). The label appears in
// user-facing names and alert subjects, so render every word with title
// casing and a space separator.
const formatMarketSlug = (slug: string): string =>
  slug
    .split(/[-_]/)
    .filter((word) => word.length > 0)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(' ');

export function hasValidLocation(query: PropertySearchQuery): boolean {
  return !!query.market || (query.zoneIds?.length ?? 0) > 0 || (query.vertices?.length ?? 0) > 0;
}

const DEFAULT_MARKET_FALLBACK = 'phoenix';

// Heuristic shape check for a routable market slug, not a membership test
// against the live market set (that set lives behind SSR/`GetActiveBuyMarkets`
// and isn't available to this pure helper). Real market identifiers are
// lowercase alphabetic words joined by hyphens/underscores (`phoenix`,
// `las-vegas`, `clarksville_ky_tn`) and never contain digits — see
// `OdProtosCommonMarket_Market` and `helpers/markets.ts`. Requiring alphabetic
// segments rejects the two values cross-client/pre-canonicalization saved
// searches realistically smuggle into `location.market`:
//   - display labels with spaces/capitals (`Downtown Phoenix`), and
//   - address-like slugs carrying street numbers / zips
//     (`825-cloister-way-mckinney-tx-75069`),
// both of which would otherwise route to a non-resolvable `/homes/<value>`.
// A surviving unknown-but-alphabetic slug still falls back gracefully: SSR
// market resolution (`tryGetMarketCoordinates` → `getServerSideProps`) returns
// `notFound`/the default market rather than erroring. zoneIds/vertices keep
// driving the result set via the query string, so the default-market base is
// safe.
const CANONICAL_MARKET_SLUG = /^[a-z]+(?:[-_][a-z]+)*$/;

// Mirrors the marketplace path conventions in `src/pages/homes/[market].tsx`:
//   single-zone   -> /homes/zid_<id>
//   market-only   -> /homes/<market>
//   multi-zone    -> /homes/<market or fallback>?zoneIds=...
export const pickPathname = (savedSearch: SavedSearchFieldsFragment): string => {
  const { zoneIds, market } = savedSearch.location;
  if (zoneIds.length === 1) {
    return `/homes/zid_${zoneIds[0]}`;
  }
  if (market && CANONICAL_MARKET_SLUG.test(market)) {
    return `/homes/${market}`;
  }
  return `/homes/${DEFAULT_MARKET_FALLBACK}`;
};

// A market-level saved search for the market already loaded on the page can be
// applied with an in-page refetch. Zone/polygon searches and cross-market
// switches need fresh SSR data (market shape, coordinates, zone resolution),
// so those callers must hard-navigate instead.
export const canApplySavedSearchInPlace = (
  savedSearch: SavedSearchFieldsFragment,
  marketIdentifier: string | null | undefined,
): boolean =>
  !!savedSearch.location.market &&
  savedSearch.location.market === marketIdentifier &&
  savedSearch.location.zoneIds.length === 0 &&
  savedSearch.location.vertices.length === 0;

export interface SaveSearchQuerySources {
  /** Canonical, location-free filter state from `useSearchState().getCanonical()`. */
  canonical: PropertySearchQuery;
  /**
   * Canonical market routing slug (`MarketInfo.identifier`) — never the display
   * `name`. On zone-based pages `identifier` is null while `name` is a
   * human-readable label (e.g. "Downtown Phoenix"); persisting that label into
   * `location.market` would later be routed verbatim to `/homes/<label>`, which
   * fails SSR market resolution. Pass the identifier (or null) so zoneIds /
   * vertices remain the location source when there is no canonical slug.
   */
  marketIdentifier: string | null | undefined;
  selectedZoneId: string | null | undefined;
  vertices: GeoPoint[] | null | undefined;
}

export function buildSaveSearchQuery({
  canonical,
  marketIdentifier,
  selectedZoneId,
  vertices,
}: SaveSearchQuerySources): PropertySearchQuery {
  return {
    ...canonical,
    market: marketIdentifier ?? null,
    zoneIds: selectedZoneId ? [selectedZoneId] : null,
    vertices: vertices && vertices.length > 0 ? vertices : null,
  };
}
